import {
  Component,
  EventEmitter,
  Input,
  Output,
  QueryList,
  ViewChildren,
  ViewContainerRef,
  ChangeDetectorRef
} from '@angular/core';

@Component({
  selector: 'axis-dual-list',
  templateUrl: './dualList.component.html'
})
export class DualListComponent {

  // selected item from any list. to know from which list selected item is, use isMoved property of item object.
  public selectedList: Array<any> = [];
  private selectedIndices: Array<any> = [];
  private lastSelectedIndex: number;
  private keys: Array<string> = [
    'arrowdown',
    'arrowup',
    'enter',
    'numpadenter',
    'space',
    'home',
    'end',
    'keya'
  ];
  public rightList = false;
  public leftList = false;
  public leftPanelSelected: boolean;
  public currentListItem: any;
  public ctrlKey = false;
  public shiftKey = false;
  public allSelected = false;
  public allSelectedCount = 0;
  public leftActiveDescendant = -1;
  public rightActiveDescendant = -1;

  @ViewChildren('dualLeftListItem') dualLeftListItem: QueryList<ViewContainerRef>;
  @ViewChildren('dualRightListItem') dualRightListItem: QueryList<ViewContainerRef>;
  @ViewChildren('verticalList') verticalListItem: QueryList<ViewContainerRef>;

  @Input() ariaLabelLeft = 'Source listbox';
  @Input() ariaLabelRight = 'Destination listbox';
  @Input() renderingLabel = 'name';
  @Input()
  set availableItems(val: Array<any>) {
    if (val) {
      this._availableItems = Array.from(val);
      this._availableItems.sort(this.dynamicSort(this.renderingLabel));
      this._availableItems.forEach((elm, ind) => {
        elm.isMoved = false;
        elm.id = ind;
      });
    }
  }
  @Input()
  set selectedItems(val: Array<any>) {
    if (val) {
      this._selectedItems = Array.from(val);
      this._selectedItems.sort(this.dynamicSort(this.renderingLabel));
      this._selectedItems.forEach((elm, ind) => {
        elm.isMoved = true;
        elm.id = this._availableItems.length + ind;
      });
    }
  }
  @Input() height: number;
  @Input() showAddBtn = true;
  @Input() showAddAllBtn = true;
  @Input() showRemoveBtn = true;
  @Input() showRemoveAllBtn = true;
  @Input() showMoveUpBtn = true;
  @Input() showMoveDownBtn = true;
  @Input() width = 150;

  public dualListItems: any = new Object();
  public _availableItems: any = []; // total avaliable items in the left list.
  public _selectedItems: any = []; // total avaliable items in the right list.

  @Output() onSelectionDone = new EventEmitter();
  @Output() getAllFocusedItems = new EventEmitter();
  @Output() getFocusedItem = new EventEmitter();

  dynamicSort(property) {
    let sortOrder = 1;
    if (property[0] === '-') {
      sortOrder = -1;
      property = property.substr(1);
    }
    return function (a, b) {
      const result =
        a[property] < b[property] ? -1 : a[property] > b[property] ? 1 : 0;
      return result * sortOrder;
    };
  }

  constructor(private changeDetectorRef: ChangeDetectorRef) { }

  /**
   * Called on click of any list item.
   * Handle single click, ctrl + click, shift + click
   * CLICK: Selects single item.
   * CTRL+CLICK: To Select multiple options by moving focus to option and press ctrl+enter to get selected.
   * SHIFT+CLICK: Selects contiguous items from the most recently selected item to the focused item.
   * @param event as HTMLObject
   * @param dualList as HTMLObject - template reference for list.
   * @param ind as number - index of the list item.
   * @param currentSelectedList as array - all available list items.
   * @param leftPanelSelected as boolean - true for left panel.
   */
  selectItems(event, dualList, ind, currentSelectedList, leftPanelSelected) {
    this.leftPanelSelected = leftPanelSelected;
    this.removeActiveClass();
    const currentItemsList = dualList.children;
    this.selectedIndices = [];
    this.setActiveDescendantOnClick(currentItemsList[ind]);
    let selected = true;

    if (event['ctrlKey']) {
      if (currentItemsList[ind].className.indexOf('active') >= 0) {
        currentItemsList[ind].classList.remove('active');
        this.selectedList = this.removeItem(
          this.selectedList,
          currentSelectedList[ind].id
        );
        this.lastSelectedIndex = undefined;
        selected = false;
      } else {
        currentItemsList[ind].classList.add('active');
        this.selectedList.push(currentSelectedList[ind]);
        this.lastSelectedIndex = ind;
      }
      this.removeFocusFromList(currentItemsList); // removes focus from other item.
      currentItemsList[ind].classList.add('focus'); // set focus to current unselected item.
    } else if (event['shiftKey']) {
      if (
        this.lastSelectedIndex === undefined ||
        this.lastSelectedIndex === ind
      ) {
        this.selectCurrentItem(currentItemsList, ind, currentSelectedList);
      } else if (this.lastSelectedIndex <= ind) {
        this.nextAll(
          this.lastSelectedIndex,
          ind,
          currentItemsList,
          currentSelectedList
        );
      } else {
        this.nextAll(
          ind,
          this.lastSelectedIndex,
          currentItemsList,
          currentSelectedList
        );
      }
    } else {
      this.selectCurrentItem(currentItemsList, ind, currentSelectedList);
    }
    this.getFocusedItem.emit({
      isLeftPanel: leftPanelSelected,
      item: JSON.parse(JSON.stringify(currentSelectedList[ind])),
      itemSelected: selected
    });

    const selectedList = JSON.parse(JSON.stringify(this.selectedList));
    this.getAllFocusedItems.emit({
      isLeftPanel: leftPanelSelected,
      items: selectedList,
      itemSelected: selected
    });
  }

  /**
   * Removes the active state of list item from left list when right list item selected and vise-versa.
   */
  private removeActiveClass() {
    if (this.leftPanelSelected) {
      this.dualRightListItem.forEach(node => {
        node['nativeElement'].classList.remove('active');
      });
    } else {
      this.dualLeftListItem.forEach(node => {
        node['nativeElement'].classList.remove('active');
      });
    }
  }

  /**
   *
   * @param currentItemsList as Array<HTMLElement> - list of all items as HTMLElement.
   * @param ind as number - index of current selection.
   * @param currentSelectedList as Array - list of all items.
   */
  selectCurrentItem(currentItemsList: Array<any>, ind: number, currentSelectedList) {
    if (!this.ctrlKey && !this.shiftKey && !this.allSelected) {
      this.selectedList = [];
      for (let i = 0; i < currentItemsList.length; i++) {
        currentItemsList[i].classList.remove('active');
        currentItemsList[i].classList.remove('focus');
      }
    }
    currentItemsList[ind].classList.add('active');
    this.selectedList.push(currentSelectedList[ind]);
    this.lastSelectedIndex = ind;
  }

  setActiveDescendantOnClick(item) {
    if (this.leftPanelSelected) {
      this.leftActiveDescendant = item.id;
    } else {
      this.rightActiveDescendant = item.id;
    }
  }

  nextAll(lowerIndex: number, higherIndex: number, currentItemsList: Array<any>, currentSelectedList: Array<any>) {
    this.selectedList = [];
    for (let i = 0; i < currentItemsList.length; i++) {
      currentItemsList[i].classList.remove('focus');
      if (i >= lowerIndex && i <= higherIndex) {
        currentItemsList[i].classList.add('active');
        this.selectedList.push(currentSelectedList[i]);
      } else {
        currentItemsList[i].classList.remove('active');
        this.selectedList.splice(i, 1);
      }
    }
  }

  removeItem(selectedLists: Array<any>, id: number) {
    const selectedListsLength = selectedLists.length;
    for (let i = 0; i < selectedListsLength; i++) {
      if (this.selectedList[i]) {
        if (selectedLists[i].id === id) {
          selectedLists.splice(i, 1);
        }
      }
    }
    return selectedLists;
  }

  addSelected() {
    this._selectedItems = this.addList(this._selectedItems);
    this._availableItems = this.removeMovedList(this._availableItems);
    this._selectedItems.sort(this.dynamicSort(this.renderingLabel));
    this.emitDualListData();
    this.resetActiveDescendentOnAdd();
    this.leftPanelSelected = undefined;
    this.currentListItem = null;
    this.changeDetectorRef.detectChanges();
  }

  addAllItems() {
    this._availableItems.forEach((element) => {
      this._selectedItems.push(element);
      this._selectedItems[this._selectedItems.length - 1].isMoved = true;
    });
    this._selectedItems.sort(this.dynamicSort(this.renderingLabel));
    this._availableItems = [];
    this.selectedList = [];
    this.emitDualListData();
    this.leftPanelSelected = undefined;
    this.currentListItem = null;
    this.changeDetectorRef.detectChanges();
  }

  removeSelected() {
    this._availableItems = this.removeList(this._availableItems);
    this._selectedItems = this.removeMovedList(this._selectedItems);
    this._availableItems.sort(this.dynamicSort(this.renderingLabel));
    this.emitDualListData();
    this.resetActiveDescendentOnRemove();
    this.leftPanelSelected = undefined;
    this.currentListItem = null;
    this.changeDetectorRef.detectChanges();
  }

  removeAll() {
    this._selectedItems.forEach((element, index) => {
      this._availableItems.push(element);
      this._availableItems[this._availableItems.length - 1].isMoved = false;
    });
    this._availableItems.sort(this.dynamicSort(this.renderingLabel));
    this._selectedItems = [];
    this.selectedList = [];
    this.emitDualListData();
    this.leftPanelSelected = undefined;
    this.currentListItem = null;
    this.changeDetectorRef.detectChanges();
  }

  moveUp() {
    if (this.selectedList.length) {
      const index = this._selectedItems.indexOf(this.selectedList[0]);
      if (index > 0) {
        const tempItem = this._selectedItems[index - 1];
        this._selectedItems[index - 1] = this.selectedList[0];
        this._selectedItems[index] = tempItem;
      }
      this.emitDualListData();
    } else {
      return;
    }
  }

  moveDown() {
    if (this.selectedList.length) {
      const index = this._selectedItems.indexOf(this.selectedList[0]);
      if (index < this._selectedItems.length - 1) {
        const tempItem = this._selectedItems[index + 1];
        this._selectedItems[index + 1] = this.selectedList[0];
        this._selectedItems[index] = tempItem;
      }
      this.emitDualListData();
    } else {
      return;
    }
  }

  addList(selectedList: Array<any>) {
    let isMovedtoOtherList = false;
    if (this.selectedList.length) {
      this.selectedList.forEach((element) => {
        if (!element.isMoved) {
          selectedList.push(element);
          selectedList[selectedList.length - 1].isMoved = true;
          isMovedtoOtherList = true;
        }
        this.selectedIndices.push(element.id);
      });
    }
    if (isMovedtoOtherList) {
      this.selectedList = [];
    }
    return selectedList;
  }

  removeList(selectedList: Array<any>) {
    let isMovedtoOtherList = false;
    this.selectedList.forEach((element) => {
      if (element.isMoved) {
        selectedList.push(element);
        selectedList[selectedList.length - 1].isMoved = false;
        isMovedtoOtherList = true;
      }
      this.selectedIndices.push(element.id);
    });
    if (isMovedtoOtherList) {
      this.selectedList = [];
    }
    return selectedList;
  }

  removeMovedList(movedList: Array<any>) {
    for (let i = 0; i < movedList.length; i++) {
      if (this.selectedIndices.indexOf(movedList[i].id) !== -1) {
        movedList.splice(i, 1);
        --i;
      }
    }
    if (this.selectedList.length === 0) {
      this.selectedIndices = [];
    }
    return movedList;
  }

  emitDualListData() {
    this.dualListItems._availableItems = this._availableItems;
    this.dualListItems._selectedItems = this._selectedItems;
    this.onSelectionDone.emit(this.dualListItems);
  }

  /**
   * Key events for the list.
   * Single selection keys:
   * TAB: moves focus to listbox and buttons.
   * ARROW_UP: Move focus to previous option.
   * ARROW_DOWN: Move focus to next option.
   * SPACE: Toggles the state of the focused option.
   * ENTER: Select the list item.
   * HOME: Selects and move focus to the first option in list.
   * END: Selects and move focus to the last option in list.
   * Multiple selection Keys:
   * CTRL + ENTER: To Select multiple options by moving focus to option and press ctrl+enter to get selected.
   * SHIFT + SPACE: Selects contiguous items from the most recently selected item to the focused item.
   * CTRL + SHIFT + HOME: Selects the focused option and all options up to the first option.
   * CTRL + SHIFT + END: Selects the focused option and all options down to the last option.
   * CTRL + A: Selects all options in the list. If all options are selected, unselects all options.
   * @param event as KeyboardEvent
   */
  keyEvent(event: KeyboardEvent) {
    const keyCode = event.code.toLowerCase();

    if (this.keys.indexOf(keyCode) > -1) {
      this.currentListItem = event.target;
      event.preventDefault();

      switch (keyCode) {
        case 'arrowdown':
        case 'arrowup':
          if (!event.shiftKey) {
            this.onArrowKeyPressed(event);
          }
          break;
        case 'numpadenter':
        case 'enter':
          if (!event.ctrlKey) {
            this.onEnterPressed();
          } else {
            this.onCtrlEnterPressed();
          }
          break;
        case 'space':
          if (!event.shiftKey) {
            this.onSpacePressed();
          } else {
            this.onShiftSpacePressed();
          }
          break;
        case 'home':
          if (!event.shiftKey && !event.ctrlKey) {
            this.onHomePressed(event);
          } else if (event.shiftKey && event.ctrlKey) {
            this.onCtrlShiftHomePressed(event);
          }
          break;
        case 'end':
          if (!event.shiftKey && !event.ctrlKey) {
            this.onEndPressed(event);
          } else if (event.shiftKey && event.ctrlKey) {
            this.onCtrlShiftEndPressed(event);
          }
          break;
        case 'keya':
          if (event.ctrlKey) {
            this.onctrlAPressed(event);
          }
          break;
      }
    }
  }

  /**
   * reset left list activeDescendant to -1, if no focused item found on adding the selected item.
   */
  private resetActiveDescendentOnAdd() {
    const focusedIndex = this.getFocusedIndex(this.dualLeftListItem['_results']);
    this.leftActiveDescendant = focusedIndex === -1 ? -1 : this.leftActiveDescendant;
  }

  /**
   * reset right list activeDescendant to -1, if no focused item found on removing the selected item.
   */
  private resetActiveDescendentOnRemove() {
    const focusedIndex = this.getFocusedIndex(this.dualRightListItem['_results']);
    this.rightActiveDescendant = focusedIndex === -1 ? -1 : this.rightActiveDescendant;
  }

  /**
   * returns the index of the focused and not selected item.
   * @param list
   * @return index: number
   */
  private getFocusedIndex(list) {
    return list.findIndex(
      element => element.nativeElement.className.indexOf('focus') > -1 &&
        element.nativeElement.className.indexOf('active') === -1
    );
  }

  /**
 * removes the focus class from the provided list.
 * @param list
 */
  private removeFocusFromList(list) {
    const length = list.length;
    for (let i = 0; i < length; i++) {
      list[i].classList.remove('focus');
    }
  }

  /**
   * When Arrow keys, Home, End keys pressed,
   * check and set the aria-activedescendant with current id of the list item.
   */
  private setActiveDescendantOnListNavigation() {
    if (this.currentListItem['parentElement'].parentElement.className.indexOf('axis-list-left') > -1) {
      this.leftActiveDescendant = parseInt(this.currentListItem.getAttribute('id'), 10);
    } else if (this.currentListItem['parentElement'].parentElement.className.indexOf('axis-list-right') > -1) {
      this.rightActiveDescendant = parseInt(this.currentListItem.getAttribute('id'), 10);
    }
  }

  /**
   * called on Arrow key pressed.
   * This will set the focus on currentListItem and set the activedescendant with current id.
   * @param event as HTMLElement
   */
  onArrowKeyPressed(event) {
    event.target.classList.remove('focus');
    if (event.target['firstElementChild']) {
      this.checkFocusIndex(event);
    } else {
      const keyCode = event.code.toLowerCase();
      if (keyCode === 'arrowdown' && event.target['nextElementSibling']) {
        this.currentListItem = event.target['nextElementSibling'];
      } else if (keyCode === 'arrowup' && event.target['previousElementSibling']) {
        this.currentListItem = event.target['previousElementSibling'];
      }
    }

    if (this.currentListItem) {
      this.currentListItem.focus();
      this.currentListItem.classList.add('focus');
      this.setActiveDescendantOnListNavigation();
    }
  }

  /**
   * When move focused on the list, this will check, if focus is on any list item.
   * If yes, then start navigation from existing focused item, else start from first item.
   * @param event as HTMLElement
   */
  checkFocusIndex(event) {
    let focusIndex;
    if (event.target.parentElement.className === 'axis-list-left' && this.dualLeftListItem['_results']) {
      focusIndex = this.dualLeftListItem['_results'].findIndex(
        element => element.nativeElement.className.indexOf('focus') > -1
      );
      this.currentListItem = focusIndex > -1 ? this.dualLeftListItem['_results'][focusIndex].nativeElement : this.currentListItem;
    } else {
      focusIndex = this.dualRightListItem['_results'].findIndex(
        element => element.nativeElement.className.indexOf('focus') > -1
      );
      this.currentListItem = focusIndex > -1 ? this.dualRightListItem['_results'][focusIndex].nativeElement : this.currentListItem;
    }
    if (focusIndex < 0) {
      this.currentListItem = event.target['firstElementChild'];
    }
  }

  /**
   * This will called on Enter pressed.
   * trigger click event on the current list item and set the active class.
   */
  onEnterPressed() {
    this.ctrlKey = false;
    this.shiftKey = false;
    this.currentListItem.click();
  }

  /**
  * This will called on Space pressed.
  * trigger click event on the current list item and set the active class.
  */
  onSpacePressed() {
    this.ctrlKey = false;
    this.shiftKey = false;
    const currentItemId = this.currentListItem.id ? parseInt(this.currentListItem.id, 10) : -1;
    if (currentItemId > -1) {
      if (this.currentListItem.className.indexOf('active') > -1) {
        this.currentListItem.classList.remove('active');
        this.currentListItem.classList.add('focus');
        this.selectedList = this.removeItem(this.selectedList, currentItemId);
      } else if (this.currentListItem.className !== 'active') {
        this.selectedList = [];
        this.currentListItem.classList.add('active');
        this.currentListItem.click();
      }
    }
  }

  onHomePressed(event) {
    this.ctrlKey = false;
    this.shiftKey = false;
    this.currentListItem = event.target['parentElement'].children[0];
    this.currentListItem.focus();
    this.setActiveDescendantOnListNavigation();
    this.currentListItem.click();
  }

  onEndPressed(event) {
    this.ctrlKey = false;
    this.shiftKey = false;
    const lastIndex = event.target['parentElement'].childNodes.length - 2;
    this.currentListItem = event.target['parentElement'].children[lastIndex];
    this.currentListItem.focus();
    this.setActiveDescendantOnListNavigation();
    this.currentListItem.click();
  }

  onCtrlEnterPressed() {
    this.ctrlKey = true;
    this.shiftKey = false;
    this.currentListItem.click();
    this.ctrlKey = false;
  }

  onShiftSpacePressed() {
    this.shiftKey = true;
    this.ctrlKey = false;
    if (this.leftPanelSelected && this.dualLeftListItem['_results']) {
      this.selectContiguousItems(this.dualLeftListItem['_results']);
    } else if (!this.leftPanelSelected && this.dualRightListItem['_results']) {
      this.selectContiguousItems(this.dualRightListItem['_results']);
    }
    this.shiftKey = false;
  }

  private selectContiguousItems(list) {
    const activeIndex = list.findIndex(element => element.nativeElement.className.indexOf('active') > -1);
    const focusIndex = list.findIndex(element => element.nativeElement.className.indexOf('focus') > -1);
    let selectedItems = [];
    if (activeIndex > focusIndex) {
      selectedItems = list.slice(focusIndex, (activeIndex + 1));
    } else {
      selectedItems = list.slice(activeIndex, (focusIndex + 1));
    }
    for (let i = 0; i < selectedItems.length; i++) {
      selectedItems[i].nativeElement.click();
    }
  }

  onCtrlShiftHomePressed(event) {
    this.shiftKey = true;
    this.ctrlKey = false;
    const focusedIndex = parseInt(this.currentListItem.getAttribute('index'), 10) + 1;
    for (let i = 0; i < focusedIndex; i++) {
      event.target['parentElement'].children[i].click();
    }
    this.currentListItem = event.target['parentElement'].children[0];
    this.currentListItem.focus();
    this.shiftKey = false;
  }

  onCtrlShiftEndPressed(event) {
    this.shiftKey = true;
    this.ctrlKey = false;
    const parentElement = event.target['parentElement'];
    const focusedIndex = parseInt(this.currentListItem.getAttribute('index'), 10) - 1;
    for (let i = parentElement.childNodes.length - 2; i > focusedIndex; i--) {
      event.target['parentElement'].children[i].click();
    }
    this.currentListItem = parentElement.children[parentElement.childNodes.length - 2];
    this.currentListItem.focus();
    this.shiftKey = false;
  }

  onctrlAPressed(event) {
    let totalActiveCount = 0;
    const length = event.target['parentElement'].children.length - 1;

    this.allSelected = true;
    this.ctrlKey = false;
    this.shiftKey = false;

    for (let i = 0; i < length; i++) {
      if (event.target['parentElement'].children[i].className.indexOf('active') > -1) {
        totalActiveCount++;
      }
    }
    const parentElement = event.target['parentElement'];
    if (this.allSelectedCount % 2 === 0) {
      this.selectAll(parentElement);
    } else {
      this.unSelectAll(parentElement);
    }
    if (totalActiveCount === 0) {
      this.selectAll(parentElement);
    } else if (totalActiveCount === parentElement.children.length - 1) {
      this.unSelectAll(parentElement);
    }
    this.allSelectedCount++;
    this.allSelected = false;
  }

  selectAll(element) {
    const length = element.children.length - 1;
    for (let i = 0; i <= length; i++) {
      element.children[i].click();
    }
  }

  unSelectAll(element) {
    const length = element.children.length - 1;
    for (let i = 0; i <= length; i++) {
      element.children[i].classList.remove('active');
      if (element.parentElement.className === 'axis-list-left') {
        this.leftPanelSelected = false;
      } else {
        this.leftPanelSelected = undefined;
      }
      this.selectedList = [];
    }
  }
}
